// English UI chrome for procurement. Master data (category names, vendor
// names, UOM codes stored in the DB) is never translated — only chrome.

const en = {
  nav: {
    procurement: "Procurement",
  },
  common: {
    reset: "Reset",
    retry: "Retry",
    cancel: "Cancel",
    close: "Close",
    save: "Save",
    search: "Search",
    export: "Export",
    columns: "Columns",
    density: "Density",
    groupBy: "Group by",
    compact: "Compact",
    comfortable: "Comfortable",
    results: (n) => `${n} result${n === 1 ? "" : "s"}`,
    rowsPerPage: "Rows per page",
    pageOf: (page, pages) => `Page ${page} of ${pages}`,
    openFullPage: "Open full page",
    notAvailableYet: "Not available yet",
    inCurrentList: "In the current list",
    loading: "Loading",
    all: "All",
    apply: "Apply",
    clearFilters: "Clear filters",
    selected: (n) => `${n} selected`,
    confirm: "Confirm",
    view: "View",
    expand: "Expand",
    collapse: "Collapse",
  },
  filter: {
    titleOrders: "Filter orders",
    titlePurchases: "Filter purchase orders",
    titlePayments: "Filter payment requests",
    view: "View",
    showFilters: "Filters",
    fiscalYear: "Fiscal year",
    month: "Month",
    center: "Center",
    department: "Department",
    category: "Category",
    searchCategory: "Search category",
    status: "Status",
    search: "Search",
    region: "Region",
    supplier: "Supplier",
    group: "Group",
    allCenters: "All centers",
    allDepartments: "All departments",
    allCategories: "All categories",
    allStatuses: "All statuses",
    allGroups: "All groups",
    allSuppliers: "All suppliers",
    urgency: "Urgency",
    allUrgency: "All urgency",
    lastUpdated: "Last updated",
    fullYear: "Full year",
    placeholder: "Filter…",
    searchOrders: "Search by order code or title…",
    searchItems: "Search by code or name…",
    searchPo: "Search by PO code, supplier or category…",
    searchPaymentRequests: "Search by PR code, PO code or supplier…",
    dateFrom: "From",
    dateTo: "To",
    item: "Item",
    allItems: "All items",
    searchItem: "Search item…",
    createdFrom: "Created from",
    createdTo: "Created to",
    noMatches: "No matches",
    invalidDateRange: "\"To\" date is before \"From\" date — this will return an empty list.",
  },
  status: {
    draft: "Draft",
    new: "New",
    pending: "Waiting for approval",
    infoRequested: "Info requested",
    approved: "Approved",
    rejected: "Rejected",
    closed: "Closed",
    issued: "Issued",
    receiving: "Receiving",
    completed: "Completed",
    cancelled: "Cancelled",
    unpaid: "Unpaid",
    partial: "Partial",
    paid: "Paid",
    overBudget: "Over budget",
    active: "Active",
    inactive: "Inactive",
    urgent: "Urgent",
    normal: "Normal",
  },
  empty: {
    noResultsTitle: "Nothing matches these filters",
    noResultsText: "Clear a filter or reset to see the full list.",
    loadErrorTitle: "Couldn't load this page",
    loadErrorText: "The service didn't respond. Try again in a moment.",
  },
  budget: {
    title: "Budget",
    subtitle: "Check what's left before you raise a request.",
    total: "Total budget",
    committed: "Committed",
    approved: "Approved",
    approvedUnpaid: "Approved – unpaid",
    approvedPaid: "Approved – paid",
    remaining: "Remaining",
    overPaid: "Over paid",
    used: "% used",
    utilization: "Utilization",
    byCenter: "By center",
    byCategory: "By category",
    byDepartment: "By department",
    byItem: "By item",
    overSpent: "Over-spent",
    overSpentHint: "Spent beyond the budget",
    overBy: (amount) => `Over by ${amount}`,
    departmentUnavailable: "Department breakdown is not available yet",
    createHint: "Need to spend from this budget? Create an order request.",
    emptyTitle: "No budget allocated for these filters",
    emptyText: "Try another year, month or center.",
    kpiHintList: "In the current list",
    remainingLow: "Less than 10% remaining",
  },
  budgetControl: {
    title: "Budget control",
    subtitle: "See what's committed, unpaid and left — before a line overruns.",
    addLine: "Add budget line",
    totalBudget: "Total budget",
    committed: "Committed",
    approvedUnpaid: "Approved – unpaid",
    approvedPaid: "Approved – paid",
    remaining: "Remaining",
    totalConsumed: "Total consumed",
    consumedTooltip: "Total consumed = committed + approved–unpaid + approved–paid",
    liabilityHint: "Liability still to pay",
    cashOutHint: "Cash already out",
    remainingHint: (pct) => `${pct}% used`,
    whereMoney: "Where the money is",
    needsAttention: "Needs attention",
    needsAttentionEmpty: "No lines near their limit in the current list.",
    pendingOnLine: "pending orders on this line",
    budgetVsActual: "Budget vs actual",
    dimension: "Dimension",
    status: "Status",
    overBudget: "Over budget",
    spendTrend: "Spend trend by month",
    trendUnavailable: "Monthly trend needs 12 periods from the current API.",
    editLinesHint: "Click a category row to view and edit its monthly budget lines.",
  },
  orders: {
    title: "Orders",
    myTitle: "My orders",
    newOrder: "Create order",
    createGroup: "Create group order",
    all: "All orders",
    grouped: "Grouped",
    awaitingMe: "Awaiting me",
    awaitingApproval: "Awaiting my approval",
    mine: "My orders",
    showGroupOrders: "Show group orders",
    sortLastUpdated: "Last updated",
    sortTotal: "Total",
    sortStatus: "Status",
    approvedThisMonth: "Approved this month",
    awaitingApprovalKpi: "Awaiting approval",
    infoRequested: "Info requested",
    rejectedThisMonth: "Rejected this month",
    orCode: "OR code",
    titleCol: "Title",
    category: "Category",
    total: "Total",
    status: "Status",
    lastUpdated: "Last updated",
    requester: "Requester",
    region: "Region",
    urgency: "Urgency",
    type: "Type",
    groupBadge: "Group",
    emptyTitle: "No order requests yet — create one to get started.",
    emptyText: "Raise a request when you need to spend against a budget.",
    emptyFilteredTitle: "No orders match these filters",
    childOrders: (n) => `${n} order${n === 1 ? "" : "s"}`,
    bulkApprove: "Approve",
    bulkReject: "Reject",
    bulkInfo: "Request info",
    bulkTitle: (action, n) => `${action} ${n} order${n === 1 ? "" : "s"}`,
    bulkBody: (n, amount) => `This will affect ${n} order${n === 1 ? "" : "s"} totalling ${amount}.`,
    drawerTitle: "Order preview",
    subtitle: "Manage and track purchase orders across centers and suppliers.",
    mySubtitle: "Track the purchase requests you have raised.",
    actions: "Actions",
  },
  purchases: {
    title: "Purchases",
    subtitle: "Manage and track purchase orders across suppliers and centers.",
    approvalSubtitle: "Purchase orders waiting on your decision.",
    createPo: "Create purchase",
    actions: "Actions",
    all: "All POs",
    awaitingApproval: "Awaiting my approval",
    openPos: "Open POs",
    awaitingKpi: "Awaiting my approval",
    receiving: "Receiving",
    unpaidValue: "Unpaid value",
    poCode: "PO code",
    created: "Created",
    region: "Region",
    category: "Category",
    supplier: "Supplier",
    total: "Total",
    status: "Status",
    paid: "Paid",
    progress: "Progress",
    issued: "Issued",
    completed: "Completed",
    emptyTitle: "No purchase orders yet",
    emptyText: "Create a PO from an approved order when you are ready to buy.",
    emptyAwaiting: "No POs awaiting your approval",
    emptyAwaitingText: "You're up to date. Switch to All POs to see the rest.",
    drawerTitle: "Purchase preview",
  },
  paymentRequests: {
    title: "Payment Requests",
    approveTitle: "Awaiting my approval",
    newPayment: "Create Payment",
    all: "All",
    awaitingMe: "Awaiting my approval",
    visiblePrs: "Visible PRs",
    pendingApproval: "Pending approval",
    prCode: "PR code",
    poCode: "PO code",
    batch: "Batch / Purpose",
    supplier: "Supplier",
    analysisCode: "Analysis code",
    center: "Center",
    invoiceDate: "Invoice date",
    invoiceSerial: "Invoice series",
    amount: "Amount",
    created: "Created",
    status: "Status",
    view: "View",
    delete: "Delete",
    emptyTitle: "No payment requests yet",
    emptyText: "Start one from the To Pay list once a purchase order is ready to settle.",
    emptyAwaitingTitle: "No PRs awaiting your approval",
    emptyAwaitingText: "You're all caught up. Clear this filter to see the full PR list.",
    emptyFilteredTitle: "No payment requests match these filters",
    deleteConfirmTitle: (code) => `Delete ${code}?`,
    deleteConfirmText: "This payment request draft will be permanently deleted. This can't be undone.",
  },
  bookInventory: {
    title: "Book Inventory",
    subtitle: "Track upcoming book demand and stock by center.",
    need7: "Need within 7 days",
    need7Hint: "Books for classes starting this week",
    need30: "Need within 30 days",
    need30Hint: "Books for classes starting this month",
    centersShort: "Centers with shortages",
    centersShortHint: "Centers short on at least one book",
    booksToReorder: "Books to reorder",
    booksToReorderHint: "Center and book lines below stock",
    filters: "Filters",
    searchPlaceholder: "Search center or book...",
    book: "Book",
    allBooks: "All books",
    window: "Demand window",
    createOrder: "Create Order Request",
    selectionHint: "Shortage quantities are prefilled. Nothing is submitted until you save the order.",
    clear: "Clear",
    emptyTitle: "No upcoming book demand",
    emptyText: "Once classes are scheduled, their book requirements will appear here.",
    deniedTitle: "You don't have permission to view book inventory",
    deniedText: "Ask a procurement admin if you need access to center stock and class demand.",
  },
  items: {
    title: "Items",
    subtitle: "Catalog, stock levels and center availability.",
    newItem: "New item",
    tabItems: "Items",
    tabGroups: "Item groups",
    tabStock: "Stock",
    tabAvailability: "Center availability",
    tabCategories: "Categories",
    activeItems: "Active items",
    itemGroups: "Item groups",
    belowReorder: "Below reorder point",
    centersCovered: "Centers covered",
    itemCode: "Item code",
    name: "Name",
    groupCategory: "Group / category",
    standardPrice: "Standard price",
    defaultVendor: "Default vendor",
    active: "Active",
    emptyItems: "No items yet",
    emptyItemsText: "Create your first item to start building the catalog.",
    emptyGroups: "No item groups yet",
    emptyGroupsText: "Group related items so requesters can find them faster.",
    emptyStock: "No stock records",
    emptyStockText: "Nothing to show for this filter.",
    emptyAvailability: "No availability rows",
    emptyAvailabilityText: "Nothing to show for this filter.",
    drawerTitle: "Item preview",
    priceHistory: "Price history",
    addPrice: "Add price",
    effectiveDate: "Effective date",
    price: "Price",
    createdBy: "Created by",
    priceVnd: "Price (VND)",
  },
  categories: {
    newCategory: "New category",
    editCategory: "Edit category",
    code: "Code",
    name: "Name",
    assetClassCode: "Asset class code",
    sunPlAccount: "SUN P&L account",
    sunBsAccount: "SUN BS account",
    active: "Active",
    allowPoWithoutOrder: "Allow PO without Order",
    allowPaymentWithoutPo: "Allow Payment without PO",
    emptyCategories: "No categories yet",
    emptyCategoriesText: "Create your first budget category to start scoping items and PRs.",
  },
  toast: {
    submitted: "Submitted for approval",
    budgetUpdated: "Budget line updated.",
    budgetCreated: "Budget line created.",
    itemCreated: "Item created.",
    itemUpdated: "Item updated.",
    categoryCreated: "Category created.",
    categoryUpdated: "Category updated.",
  },
  approval: {
    approveUpTo: (amount) => `Approve up to: ${amount}`,
    limitExceeded: (amount) =>
      `You don't have authority to approve documents above ${amount}.`,
    linkPoTitle: "Add another PO to this payment request",
    remaining: (amount) => `remaining ${amount}`,
    currentStock: (qty, uom) => `Current stock: ${qty} ${uom || ""}`.trim(),
    invalidCenters:
      "One or more lines had a center that is no longer valid for the selected region, and were removed.",
  },
}

export default en
export { en }
